import { createContext, useEffect, useReducer } from 'react';
import type { FC, ReactNode } from 'react';
import type { User } from 'src/types/user';
import SplashScreen from 'src/components/SplashScreen';
import { auth } from 'src/lib/firebase';
import authAxios from 'src/utils/authAxios';
import {
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
  createUserWithEmailAndPassword as firebaseCreateUserWithEmailAndPassword,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  sendEmailVerification as firebaseSendEmailVerification,
  reauthenticateWithCredential,
  updatePassword as firebaseUpdatePassword,
  EmailAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import type { User as FirebaseUser } from 'firebase/auth';
import { clearCompanyListCaches } from 'src/utils/companyListCache';
import { forgetPostLoginRedirect } from 'src/guards/postLoginRedirect';
import { useHistory } from 'react-router-dom';

let logoutTimer;

interface AuthState {
  isInitialised: boolean;
  isAuthenticated: boolean;
  user: User | null;
}

interface AuthContextValue extends AuthState {
  method: 'FirebaseAuth';
  createUserWithEmailAndPassword: (
    email: string,
    password: string
  ) => Promise<any>;
  signInWithEmailAndPassword: (email: string, password: string) => Promise<any>;
  signInWithGoogle: () => Promise<any>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  sendEmailVerification: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  reloadUser: () => Promise<boolean>;
  setLogoutTimer: () => any;
  logout: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

type AuthStateChangedAction = {
  type: 'AUTH_STATE_CHANGED';
  payload: {
    isAuthenticated: boolean;
    user: User | null;
  };
};

type Action = AuthStateChangedAction;

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null,
};

const reducer = (state: AuthState, action: Action): AuthState => {
  switch (action.type) {
    case 'AUTH_STATE_CHANGED': {
      const { isAuthenticated, user } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext<AuthContextValue>({
  ...initialAuthState,
  method: 'FirebaseAuth',
  createUserWithEmailAndPassword: () => Promise.resolve(),
  signInWithEmailAndPassword: () => Promise.resolve(),
  signInWithGoogle: () => Promise.resolve(),
  sendPasswordResetEmail: () => Promise.resolve(),
  sendEmailVerification: () => Promise.resolve(),
  changePassword: () => Promise.resolve(),
  reloadUser: () => Promise.resolve(false),
  setLogoutTimer: () => null,
  logout: () => Promise.resolve(),
});

// Maps a Firebase user to the app's User shape. Centralised so the auth
// listener and reloadUser() stay in sync (notably `emailVerified`).
// `hasPasswordProvider` lets settings show a change-password option only for
// email/password accounts (Google accounts have no password to change).
const mapFirebaseUser = (user: FirebaseUser): User => ({
  id: user.uid,
  avatar: user.photoURL,
  email: user.email,
  name: user.displayName || user.email,
  emailVerified: user.emailVerified,
  hasPasswordProvider: user.providerData.some(
    (provider) => provider.providerId === 'password'
  ),
  tier: 'Premium',
});

const calculateRemainingTime = (expirationTime) => {
  const currentTime = new Date().getTime();
  const adjExpirationTime = new Date(expirationTime).getTime();

  const remainingDuration = adjExpirationTime - currentTime;
  return remainingDuration;
};

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const history = useHistory();

  const signInWithEmailAndPassword = (
    email: string,
    password: string
  ): Promise<any> => {
    return firebaseSignInWithEmailAndPassword(auth, email, password);
  };

  const signInWithGoogle = (): Promise<any> => {
    const provider = new GoogleAuthProvider();
    // Without this, Google silently reuses the browser's existing Google session,
    // so users who log out can't switch to a different Google account.
    provider.setCustomParameters({ prompt: 'select_account' });

    return signInWithPopup(auth, provider).then(async (userCredential) => {
      const additionalUserInfo = getAdditionalUserInfo(userCredential);
      if (additionalUserInfo?.isNewUser) {
        try {
          const axiosRequest = await authAxios();
          await axiosRequest.post('/user/new-user-sign-up', {
            email: userCredential.user.email,
          });
        } catch {
          // Keep sign-in successful even if onboarding API is temporarily unavailable.
        }
      }

      return userCredential;
    });
  };

  ////// old token logout code
  // const setLogoutTimer = async () => {
  //   try {
  //     const expirationTime = (await firebase.auth().currentUser.getIdTokenResult()).expirationTime;
  //     const remainingTime = calculateRemainingTime(expirationTime);
  //     remainingTime > 0 ? logoutTimer = setTimeout(logout, remainingTime) : logout();
  //   }
  //   catch (err) {
  //     logout()
  //   }
  // };

  const setLogoutTimer = async () => {
    try {
      const idTokenResult = await auth.currentUser?.getIdTokenResult();
      let expirationTime = idTokenResult.expirationTime;
      let remainingTime = calculateRemainingTime(expirationTime);

      // Set the buffer time to 5 minutes before expiration
      const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds

      if (remainingTime > bufferTime) {
        logoutTimer = setTimeout(() => {
          setLogoutTimer(); // Recursively call to check and refresh token if needed
        }, remainingTime - bufferTime);
      } else {
        // Refresh the token
        const user = auth.currentUser;
        if (user) {
          await user.getIdToken(true); // Forces token refresh
          // Reset the logout timer with the new token expiration time
          const newIdTokenResult = await user.getIdTokenResult();
          expirationTime = newIdTokenResult.expirationTime;
          remainingTime = calculateRemainingTime(expirationTime);
          if (remainingTime > bufferTime) {
            logoutTimer = setTimeout(() => {
              setLogoutTimer(); // Recursively call to check and refresh token if needed
            }, remainingTime - bufferTime);
          }
        } else {
          logout();
        }
      }
    } catch (err) {
      logout();
    }
  };

  const createUserWithEmailAndPassword = async (
    email: string,
    password: string
  ): Promise<any> => {
    const userCredential = await firebaseCreateUserWithEmailAndPassword(
      auth,
      email,
      password
    );

    // Send the verification link, but never let a failed send reject sign-up:
    // the Firebase account already exists at this point, and the user can resend
    // from the verify-email screen. A bare `await` here would propagate the
    // rejection and strand the user on /register even though their account was
    // created. Server-side onboarding (/user/new-user-sign-up, which creates the
    // contact with the email vendor) is deliberately deferred until the email is
    // actually verified — see reloadUser — so unverified or fake addresses never
    // reach it.
    try {
      await firebaseSendEmailVerification(userCredential.user);
    } catch {
      // Non-fatal — the verify-email screen offers a resend.
    }

    return userCredential;
  };

  const sendPasswordResetEmail = (email: string): Promise<void> => {
    return firebaseSendPasswordResetEmail(auth, email);
  };

  const sendEmailVerification = (): Promise<void> => {
    if (!auth.currentUser) {
      return Promise.reject(new Error('No authenticated user.'));
    }
    return firebaseSendEmailVerification(auth.currentUser);
  };

  // Changes the password of an email/password account. Firebase requires a
  // recent login to update credentials, so we reauthenticate with the current
  // password first (this also validates that the current password is correct).
  const changePassword = async (
    currentPassword: string,
    newPassword: string
  ): Promise<void> => {
    const current = auth.currentUser;
    if (!current || !current.email) {
      throw new Error('No authenticated user.');
    }
    const credential = EmailAuthProvider.credential(
      current.email,
      currentPassword
    );
    await reauthenticateWithCredential(current, credential);
    await firebaseUpdatePassword(current, newPassword);
  };

  // Refreshes the current user from Firebase (e.g. after they click the
  // verification link in another tab) and re-syncs auth state. Returns the
  // latest emailVerified value.
  const reloadUser = async (): Promise<boolean> => {
    const current = auth.currentUser;
    if (!current) {
      return false;
    }
    const wasVerified = current.emailVerified;
    await current.reload();
    const refreshed = auth.currentUser;
    if (refreshed) {
      const justVerified = refreshed.emailVerified && !wasVerified;

      // The email just transitioned to verified (false -> true). reload()
      // updates the profile but NOT the cached ID token, whose email_verified
      // claim is still false — and the backend rejects every authenticated
      // route on that claim. Refresh BEFORE dispatching: the dispatch below
      // triggers the UserContext bootstrap, and authAxios sends the cached
      // token, so the verified claim must already be in place by then.
      if (justVerified) {
        try {
          await refreshed.getIdToken(true);
        } catch {
          // no-op: worst case the SDK refreshes on its own schedule
        }
      }

      dispatch({
        type: 'AUTH_STATE_CHANGED',
        payload: {
          isAuthenticated: true,
          user: mapFirebaseUser(refreshed),
        },
      });

      // Now that the user owns a confirmed email, onboard them server-side
      // (mirrors the Google sign-up flow). Best-effort: a failure here must
      // not break the verify-email flow — the signup-form submission also
      // upserts the contact.
      if (justVerified) {
        try {
          const axiosRequest = await authAxios();
          await axiosRequest.post('/user/new-user-sign-up', {
            email: refreshed.email,
          });
        } catch {
          // no-op
        }
      }
    }
    return refreshed?.emailVerified ?? false;
  };

  const logout = (): Promise<void> => {
    if (logoutTimer) clearTimeout(logoutTimer);
    indexedDB.deleteDatabase('firebaseLocalStorageDb');
    // Drop business state so nothing about this account's companies lingers
    // for the next user on a shared device.
    try {
      localStorage.removeItem('lastAccessedBusiness');
    } catch (err) {}
    clearCompanyListCaches();
    // The stash is cleared AFTER signOut: the auth change it triggers is what
    // makes AuthGuard write it, so clearing first would be undone.
    return signOut(auth).then(() => {
      forgetPostLoginRedirect();
      window.location.replace('/login');
    });
  };

  // UseEffect for every page reload
  useEffect(() => {
    // setLogoutTimer();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Here you should extract the complete user profile to make it available in your entire app.
        // The auth state only provides basic information.
        setLogoutTimer();
        dispatch({
          type: 'AUTH_STATE_CHANGED',
          payload: {
            isAuthenticated: true,
            user: mapFirebaseUser(user),
          },
        });
      } else {
        dispatch({
          type: 'AUTH_STATE_CHANGED',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    });

    return unsubscribe;
    //eslint-disable-next-line
  }, [dispatch]);

  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'FirebaseAuth',
        createUserWithEmailAndPassword,
        signInWithEmailAndPassword,
        signInWithGoogle,
        sendPasswordResetEmail,
        sendEmailVerification,
        changePassword,
        reloadUser,
        setLogoutTimer,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
